import "reflect-metadata";
import { enableProdMode } from "@angular/core";
import { platformBrowserDynamic } from "@angular/platform-browser-dynamic";

import { AppModule } from "./app/app.module";
import { environment } from "./environments/environment";
import { Config } from "./app/config";
import { LoggingService } from "./app/services/logging.service";

// Vercel Analytics and Speed Insights
import { inject as injectAnalytics } from "@vercel/analytics";
import { injectSpeedInsights } from "@vercel/speed-insights";

import "@jaak.ai/stamps";
import "@jaak.ai/visage";

// OpenTelemetry imports
import { trace, Span, SpanStatusCode } from "@opentelemetry/api";
import { WebTracerProvider, BatchSpanProcessor } from "@opentelemetry/sdk-trace-web";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
    SEMRESATTRS_SERVICE_NAME,
    SEMRESATTRS_SERVICE_VERSION,
    SEMRESATTRS_DEPLOYMENT_ENVIRONMENT,
} from "@opentelemetry/semantic-conventions";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { ZoneContextManager } from "@opentelemetry/context-zone-peer-dep";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { UserInteractionInstrumentation } from "@opentelemetry/instrumentation-user-interaction";
import { W3CTraceContextPropagator } from "@opentelemetry/core";

// Type guard para verificar si es una Request
function isRequest(request: Request | RequestInit): request is Request {
    return (request as Request).url !== undefined;
}

// Función de inicialización de OpenTelemetry
function initializeOpenTelemetry() {
    const otelConfig = environment.openTelemetry;

    if (!otelConfig?.enabled) {
        return;
    }

    try {
        // PASO 0: Instalar interceptores ANTES de crear el exporter
        const authToken = 'eAzROSTiqNd3i+M9Jw6q5Tld0jarZc617sJB//pvb5c=';

        // Deshabilitar sendBeacon globalmente porque no soporta headers personalizados
        (navigator as any).sendBeacon = function() {
            return false; // Retornar false fuerza al SDK a usar XHR/Fetch como fallback
        };

        // Interceptar XMLHttpRequest para agregar Authorization header
        const OriginalXHR = (window as any).XMLHttpRequest;

        (window as any).XMLHttpRequest = function() {
            const xhr = new OriginalXHR();
            let requestUrl = '';

            const originalOpen = xhr.open.bind(xhr);
            const originalSetRequestHeader = xhr.setRequestHeader.bind(xhr);
            const originalSend = xhr.send.bind(xhr);

            xhr.open = function(method: string, url: string, ...rest: any[]) {
                requestUrl = url.toString();
                return originalOpen(method, url, ...rest);
            };

            xhr.setRequestHeader = function(name: string, value: string) {
                return originalSetRequestHeader(name, value);
            };

            xhr.send = function(body?: any) {
                if (requestUrl.includes('collector.jaak.ai')) {
                    originalSetRequestHeader('Authorization', `Bearer ${authToken}`);
                }
                return originalSend(body);
            };

            return xhr;
        };

        Object.keys(OriginalXHR).forEach(key => {
            (window as any).XMLHttpRequest[key] = OriginalXHR[key];
        });

        // Interceptar Fetch para agregar Authorization header
        const originalFetch = window.fetch;
        window.fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
            const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;

            if (url.includes('collector.jaak.ai')) {
                const headers = new Headers(init?.headers || {});
                headers.set('Authorization', `Bearer ${authToken}`);

                init = {
                    ...init,
                    headers: headers,
                };
            }

            return originalFetch.call(this, input, init);
        };

        // 1. Crear Resource con atributos del servicio
        const resource = resourceFromAttributes({
            [SEMRESATTRS_SERVICE_NAME]: otelConfig.resourceAttributes["service.name"] || otelConfig.serviceName,
            [SEMRESATTRS_SERVICE_VERSION]: otelConfig.resourceAttributes["service.version"],
            [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: otelConfig.resourceAttributes["deployment.environment"],
            "customer.id": otelConfig.resourceAttributes["customer.id"] || "anonymous",
        });

        // 2. Configurar el exporter OTLP HTTP
        const exporter = new OTLPTraceExporter({
            url: otelConfig.endpoint,
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
            // @ts-ignore - useSendBeacon no está en los tipos pero es soportada
            useSendBeacon: false,
        });

        // 3. Crear BatchSpanProcessor
        const spanProcessor = new BatchSpanProcessor(exporter, {
            maxQueueSize: otelConfig.exporter.maxQueueSize,
            maxExportBatchSize: otelConfig.exporter.batchSize,
            scheduledDelayMillis: otelConfig.exporter.scheduledDelayMillis,
            exportTimeoutMillis: otelConfig.exporter.exportTimeoutMillis,
        });

        // 4. Crear el TracerProvider con el span processor
        const provider = new WebTracerProvider({
            resource,
            spanProcessors: [spanProcessor],
        });

        // 5. Registrar el provider globalmente
        provider.register({
            contextManager: new ZoneContextManager(),
            propagator: new W3CTraceContextPropagator(),
        });

        // 6. Configurar auto-instrumentación
        const instrumentations: any[] = [];

        if (otelConfig.instrumentation.fetch) {
            instrumentations.push(
                new FetchInstrumentation({
                    propagateTraceHeaderCorsUrls: otelConfig.propagateTraceHeaderCorsUrls,
                    clearTimingResources: true,
                    applyCustomAttributesOnSpan: (span, request, result) => {
                        if (isRequest(request) && request.url) {
                            span.setAttribute("http.url", request.url);
                        }
                        if (result instanceof Response) {
                            span.setAttribute("http.status_code", result.status);
                        }
                    },
                })
            );
        }

        if (otelConfig.instrumentation.xhr) {
            instrumentations.push(
                new XMLHttpRequestInstrumentation({
                    propagateTraceHeaderCorsUrls: otelConfig.propagateTraceHeaderCorsUrls,
                    clearTimingResources: true,
                })
            );
        }

        if (otelConfig.instrumentation.documentLoad) {
            instrumentations.push(new DocumentLoadInstrumentation());
        }

        if (otelConfig.instrumentation.userInteraction?.enabled) {
            instrumentations.push(
                new UserInteractionInstrumentation({
                    eventNames: otelConfig.instrumentation.userInteraction.eventNames as Array<keyof HTMLElementEventMap>,
                    shouldPreventSpanCreation: (eventType, element, span) => {
                        const tagName = element.tagName?.toLowerCase();
                        return tagName === "span" || tagName === "div" || tagName === "i";
                    },
                })
            );
        }

        registerInstrumentations({
            instrumentations,
        });
    } catch (error) {
        console.error("Error initializing OpenTelemetry:", error);
    }
}

// Load configuration before bootstrapping the application
async function bootstrap() {
    try {
        // Load backend.json configuration first
        await Config.loadConfig();
        console.log('[Bootstrap] Config loaded in', Config.getLoadTime(), 'ms');

        // Initialize Vercel Analytics and Speed Insights
        injectAnalytics();
        injectSpeedInsights();

        // Initialize OpenTelemetry
        initializeOpenTelemetry();

        // Use the environment configuration to determine environment mode
        if (Config.environment.environment !== "localhost") {
            enableProdMode();
        }

        // Preserve session (Capacitor Storage uses localStorage with _cap_ prefix)
        // when retrying location permissions after a reload
        if (sessionStorage.getItem('_kyc_preserve_session')) {
            sessionStorage.removeItem('_kyc_preserve_session');
        } else {
            localStorage.clear();
        }

        // Bootstrap Angular application
        await platformBrowserDynamic().bootstrapModule(AppModule);
    } catch (err) {
        LoggingService.staticLog(err, "error", true);
    }
}

// Start the application
bootstrap();
